import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Skeleton, Button, RichTextRenderer } from '@org/ui';
import type { RichTextDoc } from '@org/model';
import { ArrowLeft, Pencil } from 'lucide-react';
import { useCommunityRole } from '@/hooks/use-community-role';
import { renderMentionLink } from '@/components/mention-link';

export const Route = createFileRoute('/spaces/$spaceId/pages/$pageId/')({
  component: SubPageView,
});

function SubPageView() {
  const { spaceId, pageId } = Route.useParams();
  const { t } = useTranslation('community');
  const { isAdmin, isModerator } = useCommunityRole();

  const { data: subPage, isLoading } = communityTrpc.spaces.subPages.get.useQuery({
    subPageId: pageId,
  });

  const { data: space } = communityTrpc.spaces.getById.useQuery({ spaceId });

  const canEdit = isAdmin || isModerator(spaceId);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full rounded-2xl" />
        <div className="space-y-3">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  if (!subPage) {
    return (
      <div className="space-y-6">
        <Link to="/spaces/$spaceId" params={{ spaceId }}>
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {space?.name ?? t('subPages.backToSpace')}
          </Button>
        </Link>
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <p className="text-muted-foreground">{t('subPages.notFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to="/spaces/$spaceId" params={{ spaceId }}>
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {space?.name ?? t('subPages.backToSpace')}
          </Button>
        </Link>
        {canEdit && (
          <Link
            to="/spaces/$spaceId/pages/$pageId/edit"
            params={{ spaceId, pageId }}
          >
            <Button variant="outline" size="sm" className="gap-2" aria-label={t('subPages.edit')}>
              <Pencil className="h-4 w-4" />
              {t('subPages.edit')}
            </Button>
          </Link>
        )}
      </div>

      {subPage.bannerVideoUrl ? (
        <video
          src={subPage.bannerVideoUrl}
          controls
          preload="metadata"
          className="w-full rounded-2xl"
        />
      ) : subPage.bannerImageUrl ? (
        <img
          src={subPage.bannerImageUrl}
          alt=""
          className="w-full rounded-2xl object-cover"
        />
      ) : null}

      <div className="rounded-2xl border border-border bg-card p-6">
        <h1 className="text-2xl font-bold">{subPage.title}</h1>

        {subPage.bodyJson ? (
          <div className="mt-4">
            <RichTextRenderer value={subPage.bodyJson as RichTextDoc} renderMention={renderMentionLink} />
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">
            {t('subPages.noContent')}
          </p>
        )}
      </div>
    </div>
  );
}
